/**
 * Live view speed (noVNC side). The desktop's VNC server (x11vnc, libvncserver) has no continuous-updates extension:
 * it sends a screen update only in answer to a request, and noVNC asks for the next one only once the previous update
 * has arrived and been decoded. Every update then costs a full round trip browser → Cloudflare → computer and back, so
 * on a 60–120 ms path the screen could never refresh more than 8–15 times a second, whatever the bandwidth: that was
 * the « lag » on fiber. `pipelineUpdates` keeps a request in flight while an update is being received (the server
 * merges overlapping requests, so this never queues frames): the computer sends the next change as soon as it has it.
 *
 * Kept free of React and of the app's own imports: e2e/tools/live-view-bench.mjs loads this file as is (types stripped)
 * to measure the same code the app runs.
 */

/** JPEG quality asked of the VNC server (0..9): 6 keeps text sharp at about half the bytes of 8. */
export const VNC_QUALITY = 6;
/** zlib level asked of the server (0..9): low, the computer's CPU (half a vCPU on the smallest size) is the scarce side. */
export const VNC_COMPRESSION = 1;
/** At most this many update requests per second ahead of noVNC's own (a request is 10 bytes). */
const AHEAD_PER_SECOND = 60;

/** The parts of noVNC's RFB object used here (private in noVNC 1.7, checked at run time: a missing one disables this). */
interface RfbInternals {
  _sock?: unknown;
  _fbWidth?: number;
  _fbHeight?: number;
  _rfbConnectionState?: string;
  _enabledContinuousUpdates?: boolean;
  _framebufferUpdate?: (this: unknown) => boolean;
  _FBU?: { rects: number };
}
interface RfbClass {
  messages?: { fbUpdateRequest?: (sock: unknown, incremental: boolean, x: number, y: number, w: number, h: number) => void };
}

/**
 * Asks for the next screen update as soon as one starts arriving (noVNC asks again only once it is fully decoded).
 * Returns a function that undoes it. Safe on any noVNC: a missing internal leaves the stock behavior.
 */
export function pipelineUpdates(rfb: object, Rfb: unknown): () => void {
  const r = rfb as RfbInternals;
  const request = (Rfb as RfbClass | null)?.messages?.fbUpdateRequest;
  const original = r._framebufferUpdate;
  if (typeof request !== "function" || typeof original !== "function" || !r._FBU) return () => undefined;
  let last = 0;
  const minGap = 1000 / AHEAD_PER_SECOND;
  r._framebufferUpdate = function (this: unknown) {
    // rects === 0: the header of a new update is next. Ask for the one after it now, unless the server streams by itself.
    if (r._FBU?.rects === 0 && r._rfbConnectionState === "connected" && !r._enabledContinuousUpdates && r._fbWidth && r._fbHeight) {
      const now = performance.now();
      if (now - last >= minGap) {
        last = now;
        try {
          request(r._sock, true, 0, 0, r._fbWidth, r._fbHeight);
        } catch {
          // socket closing: noVNC reports it by itself
        }
      }
    }
    return original.call(this);
  };
  return () => {
    r._framebufferUpdate = original;
  };
}
